import { useEffect, useMemo, useState } from 'react';
import { useProjectStore } from '../../store/useProjectStore';
import { useExtractionStore } from '../../store/useExtractionStore';
import { useCollectionStore } from '../../store/useCollectionStore';
import { useAssignmentStore } from '../../store/useAssignmentStore';
import { useAuthStore } from '../../store/useAuthStore';
import { approveFileReviews, skipFileReviews } from './ExtractionRight';
import { assigneeColor } from '../../utils/assigneeColor';
import {
  EXTRACTION_DOC_TYPES,
  type ExtractionPageMeta,
  type PageClassification,
  type ApprovalRequest,
  type PageStatus,
} from '../../types';

/** Short doc-type labels for badges */
const DOC_TYPE_SHORT: Record<string, string> = {
  single_line_diagram: 'SLD',
  panel_schedule: 'Panel',
  circuit_diagram: 'Circuit',
  schematic: 'Schem',
  wiring_diagram: 'Wiring',
};

interface FileGroup {
  file: string;
  label: string;
  pages: ExtractionPageMeta[];
  zoneIds: string[];
  primaryDocType: string;
}

export function ExtractionSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedFiles, setExpandedFiles] = useState<Record<string, boolean>>({});
  const pages = useExtractionStore((s) => s.pages);
  const pageIndex = useExtractionStore((s) => s.pageIndex);
  const pageStatuses = useExtractionStore((s) => s.pageStatuses);
  const docTypeFilter = useExtractionStore((s) => s.docTypeFilter);
  const setDocTypeFilter = useExtractionStore((s) => s.setDocTypeFilter);
  const setPageIndex = useExtractionStore((s) => s.setPageIndex);
  const pendingReviews = useExtractionStore((s) => s.pendingReviews);
  const currentReview = useExtractionStore((s) => s.currentReview);
  const triage = useProjectStore((s) => s.triage);
  const separation = useProjectStore((s) => s.separation);
  const isOwner = useAuthStore((s) => s.user?.role) === 'owner';
  const assignments = useAssignmentStore((s) => s.assignments);
  const assignmentsLoaded = useAssignmentStore((s) => s.loaded);

  // Published plans + page statuses are restored centrally in init.ts/loadAllResults()

  // Build page list from triage + separation
  useEffect(() => {
    if (!triage?.classifications) return;
    const pageZoneMap: Record<string, string> = {};
    if (separation?.zones) {
      for (const z of separation.zones) {
        for (const pg of z.pages || []) {
          pageZoneMap[`${pg.file}:${pg.page}`] = z.zone_id;
        }
      }
    }

    const extPages: ExtractionPageMeta[] = triage.classifications
      .filter((c: PageClassification) => EXTRACTION_DOC_TYPES.has(c.doc_type))
      .map((c: PageClassification) => {
        const key = `${c.file}:${c.page}`;
        const zoneId = pageZoneMap[key] || 'unassigned';
        return {
          file: c.file,
          page: c.page,
          docType: c.doc_type,
          title: c.title,
          zoneId,
          status: 'pending' as PageStatus,
          classification: c,
        };
      });

    useExtractionStore.getState().setPages(extPages);
  }, [triage, separation]);

  // Auto-expand files that contain the currently selected page
  useEffect(() => {
    if (pages.length === 0) return;
    const current = pages[pageIndex];
    if (current && !expandedFiles[current.file]) {
      setExpandedFiles((prev) => ({ ...prev, [current.file]: true }));
    }
  }, [pageIndex, pages]);

  // For annotators: only show pages they are assigned to
  const myPageKeys = useMemo(() => {
    if (isOwner || !assignmentsLoaded) return null;
    const keys = new Set<string>();
    for (const a of assignments) keys.add(`${a.file}:${a.page}`);
    return keys;
  }, [isOwner, assignments, assignmentsLoaded]);

  const filteredPages = useMemo(() => {
    let result = pages;
    if (docTypeFilter) result = result.filter((p) => p.docType === docTypeFilter);
    if (myPageKeys) result = result.filter((p) => myPageKeys.has(`${p.file}:${p.page}`));
    return result;
  }, [pages, docTypeFilter, myPageKeys]);

  // Group pages by file (primary navigation level)
  const fileGroups = useMemo(() => {
    const map = new Map<string, ExtractionPageMeta[]>();
    const order: string[] = [];
    for (const p of filteredPages) {
      let arr = map.get(p.file);
      if (!arr) {
        arr = [];
        map.set(p.file, arr);
        order.push(p.file);
      }
      arr.push(p);
    }
    return order.map((file): FileGroup => {
      const filePages = map.get(file)!;
      const zoneSet = new Set(filePages.map((p) => p.zoneId));
      const typeCounts: Record<string, number> = {};
      for (const p of filePages) {
        typeCounts[p.docType] = (typeCounts[p.docType] || 0) + 1;
      }
      const primaryDocType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
      return {
        file,
        label: file.replace(/\.pdf$/i, ''),
        pages: filePages,
        zoneIds: [...zoneSet],
        primaryDocType,
      };
    });
  }, [filteredPages]);

  const pageResults = useExtractionStore((s) => s.pageResults);

  const stats = useMemo(() => {
    let done = 0, extracting = 0, reviewing = 0, pending = 0;
    for (const p of pages) {
      const key = `${p.file}:${p.page}`;
      const status = pageStatuses[key] || 'pending';
      if (status === 'done' || status === 'skipped') done++;
      else if (status === 'extracting') extracting++;
      else if (status === 'reviewing') {
        // Pages with existing data are effectively "done" for progress
        if (pageResults[key]?.new_components?.length) done++;
        else reviewing++;
      }
      else pending++;
    }
    return { done, extracting, reviewing, pending, total: pages.length };
  }, [pages, pageStatuses, pageResults]);

  const pct = stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0;

  const toggleFile = (file: string) => {
    setExpandedFiles((prev) => ({ ...prev, [file]: !prev[file] }));
  };

  if (collapsed) {
    return (
      <div className="extraction-sidebar collapsed">
        <button className="ext-sidebar-toggle" onClick={() => setCollapsed(false)} title="Expand sidebar">
          &#9656;
        </button>
        {stats.total > 0 && (
          <span className="ext-sidebar-mini-stats">{stats.done}/{stats.total}</span>
        )}
      </div>
    );
  }

  return (
    <div className="extraction-sidebar">
      <div className="extraction-sidebar-header">
        <select className="ext-type-select" value={docTypeFilter} onChange={(e) => setDocTypeFilter(e.target.value)}>
          <option value="">All types</option>
          <option value="single_line_diagram">SLD</option>
          <option value="panel_schedule">Panel</option>
          <option value="circuit_diagram">Circuit</option>
          <option value="schematic">Schematic</option>
          <option value="wiring_diagram">Wiring</option>
        </select>
        <button className="ext-sidebar-toggle" onClick={() => setCollapsed(true)} title="Collapse sidebar">
          &#9666;
        </button>
      </div>

      {stats.total > 0 && (
        <div className="extraction-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="progress-label">{pct}% ({stats.done}/{stats.total})</div>
        </div>
      )}

      {(currentReview || pendingReviews.length > 0) && (
        <div className="review-actions-row">
          <button
            className="btn-next-review"
            onClick={() => {
              const review = currentReview || pendingReviews[0];
              if (!review) return;
              const idx = pages.findIndex(p => p.file === review.file && p.page === review.page);
              if (idx >= 0) setPageIndex(idx);
            }}
          >
            Review ({pendingReviews.length + (currentReview ? 1 : 0)})
          </button>
          <ProcessAllButton />
        </div>
      )}

      <div className="extraction-zones">
        {fileGroups.map((fg) => (
          <DiagramFileGroup
            key={fg.file}
            group={fg}
            pages={pages}
            pageIndex={pageIndex}
            pageStatuses={pageStatuses}
            expanded={!!expandedFiles[fg.file]}
            toggleExpanded={() => toggleFile(fg.file)}
            setPageIndex={setPageIndex}
            currentReview={currentReview}
            pendingReviews={pendingReviews}
            isOwner={isOwner}
          />
        ))}
      </div>

      <div className="extraction-status-summary">
        <span>{stats.done} done</span>
        <span>{stats.extracting} ext</span>
        <span>{stats.reviewing} rev</span>
        <span>{stats.pending} pend</span>
      </div>

    </div>
  );
}

function DiagramFileGroup({ group, pages, pageIndex, pageStatuses, expanded, toggleExpanded, setPageIndex, currentReview, pendingReviews, isOwner }: {
  group: FileGroup;
  pages: ExtractionPageMeta[];
  pageIndex: number;
  pageStatuses: Record<string, PageStatus>;
  expanded: boolean;
  toggleExpanded: () => void;
  setPageIndex: (i: number) => void;
  currentReview: ApprovalRequest | null;
  pendingReviews: ApprovalRequest[];
  isOwner: boolean;
}) {
  const { file, label, pages: filePages, zoneIds, primaryDocType } = group;
  const [processing, setProcessing] = useState(false);

  const pageResults = useExtractionStore((s) => s.pageResults);
  const allAssignments = useAssignmentStore((s) => s.assignments);
  const fileAssignees = useMemo(() => {
    const names = new Set<string>();
    for (const a of allAssignments) {
      if (a.file === file && a.username) names.add(a.username);
    }
    return [...names];
  }, [allAssignments, file]);
  const isCollected = useCollectionStore((s) => !!s.diagrams[file]);
  const isStale = useCollectionStore((s) => s.isDiagramStale(file));
  const collectDiagram = useCollectionStore((s) => s.collectDiagram);
  const uncollectDiagram = useCollectionStore((s) => s.uncollectDiagram);

  const toggleCollect = () => {
    if (isCollected && !isStale) uncollectDiagram(file);
    else collectDiagram(file);
  };

  // Per-file progress
  const fileStats = useMemo(() => {
    let done = 0, extracting = 0;
    for (const p of filePages) {
      const key = `${p.file}:${p.page}`;
      const status = pageStatuses[key] || 'pending';
      if (status === 'done' || status === 'skipped') done++;
      else if (status === 'reviewing' && pageResults[key]?.new_components?.length) done++;
      if (status === 'extracting') extracting++;
    }
    return { done, extracting, total: filePages.length };
  }, [filePages, pageStatuses, pageResults]);

  const hasActiveExtraction = fileStats.extracting > 0;

  const filePendingCount = useMemo(() => {
    return pendingReviews.filter(r => r.file === file).length +
      (currentReview?.file === file ? 1 : 0);
  }, [file, pendingReviews, currentReview]);

  const hasReview = filePendingCount > 0;

  const hasSelectedPage = useMemo(() => {
    return filePages.some((p) => pages.indexOf(p) === pageIndex);
  }, [filePages, pages, pageIndex]);

  const handleProcess = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (processing) return;
    setProcessing(true);
    await approveFileReviews(file);
    setProcessing(false);
  };

  const handleSkip = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (processing) return;
    setProcessing(true);
    await skipFileReviews(file);
    setProcessing(false);
  };

  const docLabel = DOC_TYPE_SHORT[primaryDocType] || primaryDocType;
  const zoneLabel = zoneIds.length === 1 && zoneIds[0] !== 'unassigned' ? zoneIds[0] : '';

  return (
    <div className={`diagram-file-group ${hasSelectedPage ? 'has-selection' : ''}`}>
      <div
        className={`diagram-file-header ${hasActiveExtraction ? 'extracting' : ''}`}
        onClick={toggleExpanded}
      >
        <span className={`zone-arrow ${expanded ? 'open' : ''}`}>&#9656;</span>
        <span className="diagram-file-name" title={file}>{label}</span>
        <span className="diagram-file-meta">
          {docLabel && <span className="diagram-doc-badge">{docLabel}</span>}
          {isOwner && fileAssignees.length > 0 && (
            <span className="assignee-badges">
              {fileAssignees.map((name) => (
                <span key={name} className="assignee-badge" title={name} style={{ background: assigneeColor(name) }}>
                  {name.slice(0, 2).toUpperCase()}
                </span>
              ))}
            </span>
          )}
          <span className="diagram-page-count">{fileStats.done}/{fileStats.total}</span>
          {hasActiveExtraction && <span className="extracting-badge" />}
        </span>
      </div>

      {/* Per-file process/skip buttons — visible when file has pending reviews */}
      {hasReview && (
        <div className="file-action-bar">
          <button
            className="btn-file-process"
            onClick={handleProcess}
            disabled={processing}
            title={`Process all ${filePendingCount} pages in this document`}
          >
            {processing ? '...' : `Process (${filePendingCount})`}
          </button>
          <button
            className="btn-file-skip"
            onClick={handleSkip}
            disabled={processing}
            title="Skip this document"
          >
            Skip
          </button>
        </div>
      )}

      {fileStats.done > 0 && (
        <div className="file-collect-bar">
          <button
            className={`btn-collect ${isCollected && !isStale ? 'collected' : ''} ${isStale ? 'stale' : ''}`}
            onClick={(e) => { e.stopPropagation(); toggleCollect(); }}
          >
            {isStale ? 'Re-collect' : isCollected ? '\u2713 Collected' : 'Collect'}
          </button>
        </div>
      )}

      {expanded && (
        <div className="diagram-page-list">
          {zoneLabel && <div className="diagram-zone-label">{zoneLabel}</div>}
          {filePages.map((p) => {
            const globalIdx = pages.indexOf(p);
            const key = `${p.file}:${p.page}`;
            const status = pageStatuses[key] || 'pending';
            const hasData = !!pageResults[key]?.new_components?.length;
            const hasPendingReview = (currentReview?.file === p.file && currentReview?.page === p.page) ||
              pendingReviews.some(r => r.file === p.file && r.page === p.page);
            // Show green dot if has data (even if reviewing), pulse only for truly new reviews
            const dotStatus = (status === 'reviewing' && hasData) ? 'done' : status;
            const shouldPulse = hasPendingReview && !hasData;
            return (
              <div
                key={key}
                className={`zone-page-item ${globalIdx === pageIndex ? 'selected' : ''} ${hasPendingReview ? 'has-review' : ''}`}
                onClick={() => setPageIndex(globalIdx)}
              >
                <span className={`page-status-dot ${dotStatus} ${shouldPulse ? 'pulsing' : ''}`} />
                <span className="page-label">
                  p.{p.page}
                  {p.title && <span className="page-title"> {p.title}</span>}
                </span>
                {isOwner && <PageAssigneeBadge file={p.file} page={p.page} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProcessAllButton() {
  const pendingReviews = useExtractionStore((s) => s.pendingReviews);
  const currentReview = useExtractionStore((s) => s.currentReview);
  const [processing, setProcessing] = useState(false);

  // Get unique files with pending reviews
  const pendingFiles = useMemo(() => {
    const files = new Set<string>();
    if (currentReview?.file) files.add(currentReview.file);
    for (const r of pendingReviews) {
      if (r.file) files.add(r.file);
    }
    return [...files];
  }, [pendingReviews, currentReview]);

  if (pendingFiles.length < 2) return null;

  const handleProcessAll = async () => {
    setProcessing(true);
    // Approve all files in parallel
    await Promise.all(pendingFiles.map(file => approveFileReviews(file)));
    setProcessing(false);
  };

  return (
    <button
      className="btn-process-all"
      onClick={handleProcessAll}
      disabled={processing}
      title={`Process all ${pendingFiles.length} documents in parallel`}
    >
      {processing ? '...' : `Process All (${pendingFiles.length})`}
    </button>
  );
}

function PageAssigneeBadge({ file, page }: { file: string; page: number }) {
  const username = useAssignmentStore((s) => {
    const a = s.assignments.find((a) => a.file === file && a.page === page);
    return a?.username;
  });
  if (!username) return null;
  return (
    <span className="assignee-badge page-assignee" title={username} style={{ background: assigneeColor(username) }}>
      {username.slice(0, 2).toUpperCase()}
    </span>
  );
}
